/**
 * Splits a policy's Rego-authored PowerShellEquivalent string into copyable
 * command chunks and plain prose.
 *
 * The 198 authored values take four real shapes (verified against
 * Policies/guardian/*.rego, re-audited in the 2026-07-11 house review):
 *  - a bare PowerShell command ("Set-CsTeamsMeetingPolicy -Identity Global …")
 *  - prose the admin follows by hand ("Microsoft 365 Defender Portal >
 *    Attack Simulation Training > Simulations", "Add DNS TXT record: …")
 *  - a '#'-comment-prefixed line (37 distinct values in identity/intune):
 *    usually portal guidance ("# Configure via Entra ID portal: …"), sometimes
 *    with a real cmdlet embedded after a colon ("# Review impact before
 *    changing: Update-MgPolicyAuthorizationPolicy …")
 *  - prose with an embedded command after a colon ("Configure versioning via
 *    SharePoint site settings or PowerShell: Set-SPOSite -EnableVersioning
 *    $true")
 * Mixed prose with backtick-wrapped commands is the RemediationSteps house
 * format, not PowerShellEquivalent's — handled here defensively per the
 * blueprint should an author reuse it.
 *
 * Pure function, no DOM. The caller decides presentation: command segments
 * get the mono block + copy button, prose-only strings render as manual
 * steps with no copy affordance.
 */

export type FixSegmentKind = 'command' | 'prose'

export interface FixSegment {
  kind: FixSegmentKind
  text: string
}

export interface ParsedFixCommand {
  segments: FixSegment[]
  /** All command segment texts, in order. Empty for prose-only strings. */
  commands: string[]
  /** All prose segment texts, in order. */
  prose: string[]
}

// PowerShell Verb-Noun at the start of the string, e.g. "Set-CsTeamsMeetingPolicy",
// "Enable-SafeLinksForEmail". Prose like "Add DNS TXT record" has a space, not
// a hyphen, after the first word and does not match.
const PS_VERB_NOUN = /^[A-Z][a-z]+-[A-Z]/

// A command embedded after a prose lead-in and colon: "… or PowerShell:
// Set-SPOSite -EnableVersioning $true". The remainder must start with a
// Verb-Noun so DNS values ("Add DNS TXT record: v=spf1 …") stay prose.
const EMBEDDED_COMMAND = /^(.*?):\s+([A-Z][a-z]+-[A-Z].*)$/s

// A prose fragment that ends "… Use" / "… Run" (optionally with a colon) is a
// lead-in to the command that follows it — drop the dangling verb.
const DANGLING_LEAD_IN = /\s*\b(Use|Run):?\s*$/

function build(segments: FixSegment[]): ParsedFixCommand {
  return {
    segments,
    commands: segments.filter(s => s.kind === 'command').map(s => s.text),
    prose: segments.filter(s => s.kind === 'prose').map(s => s.text),
  }
}

export function parseFixCommand(raw: string | null | undefined): ParsedFixCommand {
  const text = (raw ?? '').trim()
  if (!text) return build([])

  // Backtick-wrapped segments are commands; the remainder is prose. An odd
  // backtick count means the wrapping is broken — treat the whole string as
  // one prose/command chunk instead of splitting mid-thought.
  const backtickCount = (text.match(/`/g) ?? []).length
  if (backtickCount >= 2 && backtickCount % 2 === 0) {
    const segments: FixSegment[] = []
    const parts = text.split('`')
    // Even indices are outside backticks (prose), odd indices are inside (command).
    parts.forEach((part, i) => {
      const isCommand = i % 2 === 1
      const trimmed = isCommand
        ? part.trim()
        : part.replace(DANGLING_LEAD_IN, '').trim()
      if (!trimmed) return
      segments.push({ kind: isCommand ? 'command' : 'prose', text: trimmed })
    })
    return build(segments)
  }

  // A leading '#' is a PowerShell comment marker on the authored value, not
  // content — strip it before classifying so "# Set-X …" is a command and
  // "# Review impact before changing: Update-X …" splits prose + command.
  const stripped = text.replace(/^#\s*/, '')
  if (!stripped) return build([])

  if (PS_VERB_NOUN.test(stripped)) {
    return build([{ kind: 'command', text: stripped }])
  }

  const embedded = stripped.match(EMBEDDED_COMMAND)
  if (embedded) {
    const lead = embedded[1].trim()
    const command = embedded[2].trim()
    const segments: FixSegment[] = []
    if (lead) segments.push({ kind: 'prose', text: lead })
    segments.push({ kind: 'command', text: command })
    return build(segments)
  }

  return build([{ kind: 'prose', text: stripped }])
}
